// SPDX-License-Identifier: Apache-2.0
import GirafeHTMLElement from '../../base/GirafeHTMLElement';
import {
  AVAILABLE_CELL_COMPONENTS,
  CellContent,
  getGridSize,
  LayoutType,
  MAX_CELL_SIZE,
  MIN_CELL_SIZE
} from '../../tools/state/layout';

/** A splitter, placed on the boundary between two columns or two rows of the Grid. */
type Splitter = {
  /** Whether the splitter is dragged left/right (vertical) or up/down (horizontal). */
  orientation: 'vertical' | 'horizontal';
  /** Index of the column (vertical) or of the row (horizontal) placed before the splitter. */
  index: number;
  /** Position of the splitter, in percent of the Grid. */
  position: number;
};

/**
 * The Grid in which the Map Components are displayed.
 *
 * The Components are written as children of this Element in the HTML of the application
 * and are moved into the Cells of the Grid, depending on <code>state.layout</code>.
 * A Component that is not placed in any Cell is not slotted, and therefore not displayed.
 */
class MapGridComponent extends GirafeHTMLElement {
  templateUrl = './template.html';
  styleUrls = ['../../styles/common.css', './style.css'];

  /** Index of each Cell of the current Layout, used to render the Cells and their slots. */
  cellIndexes: number[] = [];
  /** The splitters between the Cells of the current Layout. */
  splitters: Splitter[] = [];

  /** The splitter that is currently dragged, if any. */
  private draggedSplitter: Splitter | null = null;

  public constructor() {
    super('map-grid');
  }

  private get layout() {
    return this.state.layout;
  }

  private get grid() {
    return this.getById('grid');
  }

  protected override connectedCallback() {
    super.connectedCallback();
    this.render();
    // The subscription on layout.type is called immediately, which builds the Grid.
    this.registerEvents();
  }

  private registerEvents() {
    this.subscribe('layout.type', () => this.applyLayout());
    this.subscribe('layout.cells', () => this.applyCells());
    this.subscribe('layout.sizes.columns', () => this.applySizes());
    this.subscribe('layout.sizes.rows', () => this.applySizes());
  }

  /**
   * Rebuilds the Cells and the splitters of the Grid for the current Layout,
   * then places the Components and applies the sizes.
   */
  private applyLayout() {
    const { columns, rows } = getGridSize(this.layout.type as LayoutType);
    this.cellIndexes = Array.from({ length: columns * rows }, (_, index) => index);
    this.splitters = this.createSplitters(columns, rows);
    this.refreshRender();
    this.applyCells();
    this.applySizes();
  }

  /**
   * Creates one splitter for each boundary between two columns and between two rows.
   * The splitters go through the whole Grid, so that the columns and the rows keep
   * the same sizes in every Cell of the Grid.
   */
  private createSplitters(columns: number, rows: number): Splitter[] {
    const splitters: Splitter[] = [];
    for (let index = 0; index < columns - 1; index++) {
      splitters.push({ orientation: 'vertical', index: index, position: 0 });
    }
    for (let index = 0; index < rows - 1; index++) {
      splitters.push({ orientation: 'horizontal', index: index, position: 0 });
    }
    return splitters;
  }

  /**
   * Moves each Component into the Cell it is configured for, by setting the name of the slot
   * it must be displayed in. Components that are not placed in any Cell get no slot,
   * which means that they are not displayed at all.
   */
  private applyCells() {
    const cells = this.layout.cells as CellContent[];
    for (const component of AVAILABLE_CELL_COMPONENTS) {
      const element = this.querySelector(component.tagName);
      if (!element) {
        continue;
      }
      const cellIndex = cells.indexOf(component.id);
      if (cellIndex >= 0) {
        element.setAttribute('slot', `cell-${cellIndex}`);
      } else {
        element.removeAttribute('slot');
      }
    }
  }

  /** Applies the sizes of the columns and of the rows to the Grid and to the splitters. */
  private applySizes() {
    const grid = this.grid;
    if (!grid) {
      return;
    }
    const { columns, rows } = this.layout.sizes;
    grid.style.gridTemplateColumns = columns.map((size: number) => `${size}%`).join(' ');
    grid.style.gridTemplateRows = rows.map((size: number) => `${size}%`).join(' ');

    for (const splitter of this.splitters) {
      const sizes = splitter.orientation === 'vertical' ? columns : rows;
      splitter.position = sizes.slice(0, splitter.index + 1).reduce((sum: number, size: number) => sum + size, 0);
      this.applySplitterPosition(splitter);
    }
  }

  private applySplitterPosition(splitter: Splitter) {
    const element = this.getById(this.getSplitterId(splitter));
    if (!element) {
      return;
    }
    // When the Layout changes, uHtml reuses the Elements of the previous splitters, which can have
    // the other orientation. The position of the other orientation has therefore to be removed,
    // otherwise it would override the size the splitter gets from its stylesheet.
    const isVertical = splitter.orientation === 'vertical';
    element.style.left = isVertical ? `${splitter.position}%` : '';
    element.style.top = isVertical ? '' : `${splitter.position}%`;
  }

  getSplitterId(splitter: Splitter) {
    return `splitter-${splitter.orientation}-${splitter.index}`;
  }

  onSplitterPointerDown(event: PointerEvent, splitter: Splitter) {
    event.preventDefault();
    this.draggedSplitter = splitter;
    (event.target as HTMLElement).setPointerCapture(event.pointerId);
  }

  /**
   * Resizes the two columns (or the two rows) around the dragged splitter.
   * While dragging, the new sizes are only applied to the DOM: the state is updated
   * at the end of the drag, to prevent flooding it with intermediate values.
   */
  onSplitterPointerMove(event: PointerEvent, splitter: Splitter) {
    if (this.draggedSplitter !== splitter) {
      return;
    }
    const gridBounds = this.grid.getBoundingClientRect();
    const isVertical = splitter.orientation === 'vertical';
    const position = isVertical
      ? ((event.clientX - gridBounds.left) / gridBounds.width) * 100
      : ((event.clientY - gridBounds.top) / gridBounds.height) * 100;

    const sizes = this.getResizedSizes(splitter, position);
    if (!sizes) {
      return;
    }

    splitter.position = sizes.slice(0, splitter.index + 1).reduce((sum, size) => sum + size, 0);
    this.applySplitterPosition(splitter);
    const cssSizes = sizes.map((size) => `${size}%`).join(' ');
    if (isVertical) {
      this.grid.style.gridTemplateColumns = cssSizes;
    } else {
      this.grid.style.gridTemplateRows = cssSizes;
    }
  }

  onSplitterPointerUp(event: PointerEvent, splitter: Splitter) {
    if (this.draggedSplitter !== splitter) {
      return;
    }
    this.draggedSplitter = null;
    (event.target as HTMLElement).releasePointerCapture(event.pointerId);

    const sizes = this.getResizedSizes(splitter, splitter.position);
    if (sizes) {
      if (splitter.orientation === 'vertical') {
        this.layout.sizes.columns = sizes;
      } else {
        this.layout.sizes.rows = sizes;
      }
    }
  }

  /**
   * Computes the new sizes of the columns (or of the rows) when the splitter is moved
   * to the given position, in percent of the Grid. The two resized columns (or rows) are
   * kept between MIN_CELL_SIZE and MAX_CELL_SIZE, the other ones keep their size.
   */
  private getResizedSizes(splitter: Splitter, position: number): number[] | null {
    const sizes = [
      ...(splitter.orientation === 'vertical' ? this.layout.sizes.columns : this.layout.sizes.rows)
    ] as number[];
    const before = splitter.index;
    const after = splitter.index + 1;

    // Size the splitter can be dragged in, without taking the columns/rows that are not resized into account.
    const offset = sizes.slice(0, before).reduce((sum, size) => sum + size, 0);
    const available = sizes[before] + sizes[after];

    let newSizeBefore = position - offset;
    newSizeBefore = Math.max(MIN_CELL_SIZE, Math.min(MAX_CELL_SIZE, newSizeBefore));
    newSizeBefore = Math.min(newSizeBefore, available - MIN_CELL_SIZE);
    newSizeBefore = Math.max(newSizeBefore, available - MAX_CELL_SIZE);

    sizes[before] = newSizeBefore;
    sizes[after] = available - newSizeBefore;
    return sizes;
  }

  override render() {
    super.render();
    super.girafeTranslate();
  }
}

export default MapGridComponent;
